import { SearchOutlined } from '@ant-design/icons';
import {
  Card,
  Col,
  Empty,
  Flex,
  Input,
  Radio,
  Select,
  Spin,
  Typography,
} from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { useGetMarketplaces } from '../../../../utils/api/marketplaces/useGetMarketplaces';
import { useGetMpCategories } from '../../../../utils/api/mpCategories/useGetMpCategories';

const { Text } = Typography;

const MarketplaceCategoryColumn = ({ onCategorySelect, selectedCategory }) => {
  // Récupération des marketplaces depuis l'API
  const { marketplaces, isLoading: isLoadingMarketplaces } =
    useGetMarketplaces();

  // Transformer les marketplaces pour le Select
  const marketplaceOptions = useMemo(() => {
    if (!marketplaces) return [];
    return marketplaces.map((mp) => ({
      label: mp.name,
      value: mp.id,
    }));
  }, [marketplaces]);

  // État pour la sélection de marketplace (null = aucune sélection)
  const [selectedMarketplace, setSelectedMarketplace] = useState(null);
  // État pour la recherche de catégories
  const [categorySearch, setCategorySearch] = useState('');

  // Récupérer le nom de la marketplace sélectionnée pour le filtre API
  const selectedMarketplaceName = useMemo(() => {
    if (!selectedMarketplace || !marketplaces) return null;
    const mp = marketplaces.find((m) => m.id === selectedMarketplace);
    return mp?.name || null;
  }, [selectedMarketplace, marketplaces]);

  // Récupération des catégories marketplace depuis l'API (filtrées par marketplace)
  const { mpCategories, isLoading: isLoadingCategories } = useGetMpCategories(
    { marketplace__name: selectedMarketplaceName },
    { enabled: !!selectedMarketplaceName }
  );

  // Récupérer les infos de la marketplace sélectionnée
  const _selectedMarketplaceInfo = useMemo(
    () => marketplaceOptions.find((mp) => mp.value === selectedMarketplace),
    [marketplaceOptions, selectedMarketplace]
  );

  // Filtrer les catégories par recherche (le filtre marketplace est déjà appliqué via l'API)
  const filteredCategories = useMemo(() => {
    if (!mpCategories || mpCategories.length === 0) return [];
    let categories = mpCategories.map((cat) => ({
      key: cat.id,
      id: cat.id,
      name: cat.name,
      label: cat.label,
    }));

    // Appliquer le filtre de recherche
    if (categorySearch.trim()) {
      const query = categorySearch.toLowerCase().trim();
      categories = categories.filter(
        (cat) =>
          cat.name?.toLowerCase().includes(query) ||
          cat.label?.toLowerCase().includes(query)
      );
    }

    return categories;
  }, [mpCategories, categorySearch]);

  // Gestion du changement de marketplace
  const handleMarketplaceChange = useCallback(
    (value) => {
      setSelectedMarketplace(value);
      onCategorySelect?.(null); // Reset la catégorie sélectionnée
      setCategorySearch(''); // Reset la recherche
    },
    [onCategorySelect]
  );

  // Gestion de la sélection de catégorie
  const handleCategorySelect = useCallback(
    (categoryId) => {
      const category = filteredCategories.find((cat) => cat.id === categoryId);
      onCategorySelect?.(category);
    },
    [filteredCategories, onCategorySelect]
  );

  return (
    <Col
      span={12}
      style={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'calc(100vh - 250px)',
        minHeight: 300,
      }}
    >
      <Card
        size="small"
        title={
          <Flex align="center" justify="space-between">
            <Text strong>Catégorie Marketplace</Text>
          </Flex>
        }
        styles={{
          body: {
            padding: 0,
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        {/* Sélecteur de marketplace */}
        <div style={{ padding: 12, borderBottom: '1px solid #f0f0f0' }}>
          <Flex vertical gap={12}>
            <div>
              <Text
                type="secondary"
                style={{ fontSize: 12, marginBottom: 4, display: 'block' }}
              >
                1. Sélectionner une marketplace
              </Text>
              <Select
                placeholder="Choisir une marketplace"
                value={selectedMarketplace}
                onChange={handleMarketplaceChange}
                loading={isLoadingMarketplaces}
                options={marketplaceOptions.map((mp) => ({
                  label: mp.label,
                  value: mp.value,
                }))}
                style={{ width: '100%' }}
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const mp = marketplaceOptions.find(
                    (m) => m.value === option.value
                  );
                  return mp?.label?.toLowerCase().includes(input.toLowerCase());
                }}
              />
            </div>

            {/* Champ de recherche - visible uniquement si marketplace sélectionnée */}
            {selectedMarketplace && (
              <div>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 12,
                    marginBottom: 4,
                    display: 'block',
                  }}
                >
                  2. Rechercher et sélectionner une catégorie
                </Text>
                <Input
                  allowClear
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  placeholder="Rechercher une catégorie..."
                  prefix={<SearchOutlined />}
                />
              </div>
            )}
          </Flex>
        </div>

        {/* Zone de contenu : liste des catégories ou états vides */}
        <div
          style={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {!selectedMarketplace ? (
            // Aucune marketplace sélectionnée
            <Flex
              align="center"
              justify="center"
              style={{ flex: 1, padding: 24 }}
            >
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Sélectionnez une marketplace pour afficher les catégories"
              />
            </Flex>
          ) : isLoadingCategories ? (
            // Chargement des catégories
            <Flex
              align="center"
              justify="center"
              style={{ flex: 1, padding: 24 }}
            >
              <Spin tip="Chargement des catégories..." />
            </Flex>
          ) : filteredCategories.length === 0 ? (
            // Aucune catégorie trouvée
            <Flex
              align="center"
              justify="center"
              style={{ flex: 1, padding: 24 }}
            >
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  categorySearch
                    ? `Aucune catégorie trouvée pour "${categorySearch}"`
                    : 'Aucune catégorie disponible pour cette marketplace'
                }
              />
            </Flex>
          ) : (
            // Liste des catégories avec Radio
            <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
              <Flex align="center" gap={8} style={{ marginBottom: 12 }}>
                <Text type="secondary">
                  {filteredCategories.length} catégorie
                  {filteredCategories.length > 1 ? 's' : ''} disponible
                  {filteredCategories.length > 1 ? 's' : ''}
                </Text>
              </Flex>

              <Radio.Group
                value={selectedCategory?.id}
                onChange={(e) => handleCategorySelect(e.target.value)}
                style={{ width: '100%' }}
              >
                <Flex vertical gap={8}>
                  {filteredCategories.map((category) => {
                    const isSelected = selectedCategory?.id === category.id;
                    return (
                      <Card
                        key={category.key}
                        size="small"
                        hoverable
                        style={{
                          cursor: 'pointer',
                          border: isSelected
                            ? '2px solid #1890ff'
                            : '1px solid #f0f0f0',
                          backgroundColor: isSelected ? '#e6f7ff' : undefined,
                          transition: 'all 0.2s ease',
                        }}
                        styles={{ body: { padding: 12 } }}
                        onClick={() => handleCategorySelect(category.id)}
                      >
                        <Flex align="flex-start" gap={12}>
                          <Radio value={category.id} style={{ marginTop: 2 }} />
                          <Flex
                            vertical
                            gap={2}
                            style={{ flex: 1, minWidth: 0 }}
                          >
                            <Text strong>{category.label}</Text>
                            <Text
                              type="secondary"
                              style={{ fontSize: 12 }}
                              ellipsis={{ tooltip: category.name }}
                            >
                              {category.name}
                            </Text>
                          </Flex>
                        </Flex>
                      </Card>
                    );
                  })}
                </Flex>
              </Radio.Group>
            </div>
          )}
        </div>
      </Card>
    </Col>
  );
};

export default MarketplaceCategoryColumn;
